import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  UncontrolledTooltip,
  CardTitle,
  Input,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  DEFAULT_RECORDS_PER_PAGE,
  fixedWidthCustomStyle,
} from "constants/CommonConstants";
import { formatDate } from "components/CommonComponents/CommonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { getIssuanceList } from "../../../action/issuance";
import { issuanceList as issuanceListAction } from "../../../constants/issuance";
import { getActiveCourseNameList } from "action/batch";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";

const IssuanceList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const issuanceDataList = useSelector((state) => state.issuance.issuanceList);
  const courseNameListData = useSelector(
    (state) => state.batch.activeCourseNameList,
  );

  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;

  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [filterState, setFilterState] = useState({
    searchName: "",
    courseName: null,
    batchDate: [null, null],
    completionDate: [null, null],
    filterParams: [],
  });

  useEffect(() => {
    getActiveCourseNameList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getIssuanceList(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({ type: issuanceListAction, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (issuanceDataList?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: issuanceDataList?.data?.Batches?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (issuanceDataList?.code || issuanceDataList?.message) {
      setListState({
        isLoading: false,
        error:
          issuanceDataList?.message ||
          "Failed to load issuance list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [issuanceDataList]);

  const courseOptions =
    courseNameListData?.data?.map((course) => ({
      value: course.ID,
      label: course.courseName,
    })) || [];

  const tableData = issuanceDataList?.data?.Batches?.map((item, index) => {
    return {
      ...item,
      id: item.ID,
      srNo:
        (pageNum - 1) *
        (issuanceDataList?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    };
  });

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.courseName?.value) {
      filterParams.push({
        field: "CourseID",
        condition: "eq",
        filterValues: [String(filterState.courseName.value)],
      });
    }

    if (filterState.batchDate[0]) {
      if (filterState.batchDate[1]) {
        filterParams.push({
          field: "BatchStartDate",
          condition: "btw",
          filterValues: [
            formatDate(filterState.batchDate[0]),
            formatDate(filterState.batchDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "BatchStartDate",
          condition: "eq",
          filterValues: [formatDate(filterState.batchDate[0])],
        });
      }
    }

    if (filterState.completionDate[0]) {
      if (filterState.completionDate[1]) {
        filterParams.push({
          field: "BatchCompletionDate",
          condition: "btw",
          filterValues: [
            formatDate(filterState.completionDate[0]),
            formatDate(filterState.completionDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "BatchCompletionDate",
          condition: "eq",
          filterValues: [formatDate(filterState.completionDate[0])],
        });
      }
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setFilterState({
      searchName: "",
      courseName: null,
      batchDate: [null, null],
      completionDate: [null, null],
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Course Name",
      accessor: "courses.courseName",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Batch Name",
      accessor: "name",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Batch Start Date",
      accessor: "batchStartDate",
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Batch End Date",
      accessor: "batchEndDate",
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Batch Completion Date",
      accessor: "batchCompletionDate",
      minWidth: 160,
      maxWidth: 160,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Actions",
      minWidth: 100,
      maxWidth: 100,
      disableSortBy: true,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.ID);
        const tooltipId = `details${encodedId}`;

        return (
          <div className="d-flex align-items-center">
            <Button
              className="btn-link p-0 mr-2"
              id={tooltipId}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();

                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/certificate/issuance/${encodedId}`
                    : `/admin/certificate/issuance/${encodedId}`,
                );
              }}
            >
              <i className="fas fa-users mr-1" />
            </Button>
            <UncontrolledTooltip target={tooltipId}>
              View Issuance Details
            </UncontrolledTooltip>

            <Button
              className="btn-link p-0"
              id={`certs${tooltipId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();

                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/certificate/issuance/${encodedId}/certificates`
                    : `/admin/certificate/issuance/${encodedId}/certificates`,
                );
              }}
            >
              <i className="fas fa-file-contract mr-1" />
            </Button>
            <UncontrolledTooltip target={`certs${tooltipId}`}>
              View Issued Certificates
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />

      <Container className="mt--6" fluid>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Certificate Issuance
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={courseOptions}
                      placeholder="Select course name"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.courseName}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          courseName: selected,
                        }))
                      }
                    />
                    <Input
                      type="text"
                      placeholder="Search by batch name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />
                    <DatePicker
                      onChangeRaw={(e) => e.preventDefault()}
                      selected={filterState.batchDate?.[0]}
                      onChange={(dates) =>
                        setFilterState((prev) => ({
                          ...prev,
                          batchDate: dates,
                        }))
                      }
                      startDate={filterState.batchDate?.[0]}
                      endDate={filterState.batchDate?.[1]}
                      selectsRange
                      onKeyDown={handleKeyDown}
                      isClearable={!!filterState.batchDate?.[0]}
                      placeholderText="Select batch date or range"
                      className="form-control mr-2 mb-2 filter-input-large"
                      dateFormat="dd-MMM-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      calendarClassName="custom-calendar"
                      onPaste={(e) => e.preventDefault()}
                    />
                    <DatePicker
                      onChangeRaw={(e) => e.preventDefault()}
                      selected={filterState.completionDate?.[0]}
                      onChange={(dates) =>
                        setFilterState((prev) => ({
                          ...prev,
                          completionDate: dates,
                        }))
                      }
                      startDate={filterState.completionDate?.[0]}
                      endDate={filterState.completionDate?.[1]}
                      selectsRange
                      onKeyDown={handleKeyDown}
                      isClearable={!!filterState.completionDate?.[0]}
                      placeholderText="Select completion date or range"
                      className="form-control mr-2 mb-2 filter-input-large"
                      dateFormat="dd-MMM-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      calendarClassName="custom-calendar"
                      onPaste={(e) => e.preventDefault()}
                    />
                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""
                        }`}
                    >
                      {listState.isSearched ? (
                        <>
                          <i className="fa fa-spinner fa-spin loader fa-xl" />{" "}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" /> Search
                        </>
                      )}
                    </Button>
                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {issuanceDataList?.data?.totalBatchesRecords > 0 ? (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {issuanceDataList?.data?.totalFilteredRecords}{" "}
                          of {issuanceDataList?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {issuanceDataList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        issuanceDataList?.data?.totalFilteredBatchesRecords /
                        (issuanceDataList?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default IssuanceList;
